export interface NavigationTab {
  show?: string | null;
  icon?: string | null;
  label?: string | null;
  screen?: string | null;
}

export interface StudentTab {
  /** Rota ja resolvida — a screen do servidor passou por routeForScreen. */
  screen: string;
  label: string;
  icon: string;
}

// Catalogo de telas que o app do aluno conhece. A conta escolhe QUAIS aparecem,
// em que ordem e com que rotulo/icone — isso vem da API. Este mapa so define o
// que existe e o fallback de rotulo/icone quando a conta nao personaliza.
export const SCREEN_CATALOG: Record<string, {label: string; icon: string}> = {
  social: {label: 'Home', icon: 'fa-home'},
  wodListDate: {label: 'Treinos', icon: 'fa-dumbbell'},
  wodList: {label: 'Lista de Treinos', icon: 'fa-list'},
  wods: {label: 'Treinos', icon: 'fa-dumbbell'},
  trainer: {label: 'Treinos', icon: 'fa-dumbbell'},
  schedule: {label: 'Agenda', icon: 'fa-calendar'},
  courses: {label: 'Cursos', icon: 'fa-graduation-cap'},
  records: {label: 'Recordes', icon: 'fa-trophy'},
  favorites: {label: 'Favoritos', icon: 'fa-heart'},
  completions: {label: 'Completados', icon: 'fa-check'},
  timer: {label: 'Timer', icon: 'fa-stopwatch'},
  anthropometrics: {label: 'Medidas', icon: 'fa-weight-scale'},
  messages: {label: 'Mensagens', icon: 'fa-message'},
  challenges: {label: 'Desafios', icon: 'fa-medal'},
  menu: {label: 'Menu', icon: 'fa-user'},
  profile: {label: 'Menu', icon: 'fa-user'},
};

// No app-mobile o menu vive na screen `profile`: a API manda `screen: 'menu'` e
// o roteador resolve para a rota `profile` (resolveRouteName em
// src/app/(auth)/(tabs)/_layout.tsx). Manter a mesma resolucao aqui e o que faz
// uma conta ja configurada continuar valendo sem mudar nada no banco.
//
// Hoje todas as contas mandam 'menu', mas `profile` entra no catalogo acima
// para que configurar a screen direto tambem caia no rotulo/icone certo.
const ROUTE_ALIASES: Record<string, string> = {menu: 'profile'};

// A substituicao acontece uma unica vez, ao ler a resposta do servidor em
// buildTabs. Depois disso `StudentTab.screen` ja e a rota — nenhum componente
// precisa lembrar de traduzir, o que evita o caso de um call site esquecido
// mandar a aba para uma rota inexistente.
export const routeForScreen = (screen: string): string => ROUTE_ALIASES[screen] ?? screen;

// A API manda `show` como 'true' | 'false' | 'NAV_n_SHOW'. So 'false' esconde —
// qualquer outro valor (inclusive os placeholders NAV_n_SHOW) e visivel, que e
// como o app mobile interpreta.
const isVisible = (tab: NavigationTab): boolean => String(tab.show) !== 'false';

// Mesma ordem e mesmos rotulos do fallback do app-mobile
// (src/app/(auth)/(tabs)/_layout.tsx): a conta sem app configurado tem que
// abrir igual nas duas plataformas. Em pt-BR o mobile traduz "Home" como
// "Home" mesmo, entao nao trocar por "Inicio".
const DEFAULT_TABS: StudentTab[] = [
  {screen: 'social', label: 'Home', icon: 'fa-home'},
  {screen: routeForScreen('menu'), label: 'Menu', icon: 'fa-user'},
  {screen: 'challenges', label: 'Desafios', icon: 'fa-medal'},
];

const normalizeIcon = (icon?: string | null): string | null => {
  if (!icon) return null;

  return icon.startsWith('fa-') ? icon : `fa-${icon}`;
};

export const buildTabs = (tabs?: NavigationTab[] | null): StudentTab[] => {
  const configuradas = (tabs ?? [])
    .filter((tab) => tab.screen && isVisible(tab))
    .map((tab) => {
      // O catalogo e consultado pelo nome que veio do servidor; a rota e a
      // versao ja substituida.
      const doServidor = tab.screen as string;
      const fallback = SCREEN_CATALOG[doServidor];

      return {
        screen: routeForScreen(doServidor),
        label: tab.label?.trim() || fallback?.label || doServidor,
        icon: normalizeIcon(tab.icon) || fallback?.icon || 'fa-circle',
      };
    });

  // Conta pode repetir a mesma tela em posicoes diferentes; a primeira vence.
  const vistas = new Set<string>();
  const unicas = configuradas.filter((tab) => {
    if (vistas.has(tab.screen)) return false;
    vistas.add(tab.screen);

    return true;
  });

  return unicas.length > 0 ? unicas : DEFAULT_TABS;
};

// Toda tela conhecida vira rota, e nao so as abas visiveis: o menu leva a
// telas que a conta tirou da barra. As abas vem primeiro para preservar
// rotulo e icone escolhidos pela conta; o resto entra com o do catalogo.
export const buildRoutes = (tabs: StudentTab[]): StudentTab[] => {
  const rotas = [...tabs];
  const jaTem = new Set(tabs.map((tab) => tab.screen));

  Object.entries(SCREEN_CATALOG).forEach(([screen, dados]) => {
    // `menu` e `profile` resolvem para a mesma rota; sem deduplicar, o React
    // Router receberia duas entradas iguais.
    const rota = routeForScreen(screen);
    if (jaTem.has(rota)) return;

    jaTem.add(rota);
    rotas.push({screen: rota, ...dados});
  });

  return rotas;
};
